// Low-level helpers for reading bytes and bits while recording Fields.
import type { Field } from './types';

const latin1 = new TextDecoder('latin1');
const utf8 = new TextDecoder('utf-8');
const utf16le = new TextDecoder('utf-16le');
const utf16be = new TextDecoder('utf-16be');

export function ascii(b: Uint8Array, start: number, len: number): string {
  return latin1.decode(b.subarray(start, Math.min(b.length, start + len)));
}
export function utf8Str(b: Uint8Array, start: number, len: number): string {
  return utf8.decode(b.subarray(start, Math.min(b.length, start + len)));
}
export function utf16Str(b: Uint8Array, start: number, len: number, bigEndian = false): string {
  return (bigEndian ? utf16be : utf16le).decode(b.subarray(start, Math.min(b.length, start + len)));
}
/** True when the bytes at `at` equal the given ASCII string. */
export function matches(b: Uint8Array, at: number, s: string): boolean {
  if (at < 0 || at + s.length > b.length) return false;
  for (let i = 0; i < s.length; i++) if (b[at + i] !== s.charCodeAt(i)) return false;
  return true;
}

export const u8 = (b: Uint8Array, o: number) => b[o];
export const u16be = (b: Uint8Array, o: number) => (b[o] << 8) | b[o + 1];
export const u16le = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
export const u24be = (b: Uint8Array, o: number) => (b[o] << 16) | (b[o + 1] << 8) | b[o + 2];
export const u24le = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8) | (b[o + 2] << 16);
export const u32be = (b: Uint8Array, o: number) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
export const u32le = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
export const u64be = (b: Uint8Array, o: number) => u32be(b, o) * 4294967296 + u32be(b, o + 4);
export const u64le = (b: Uint8Array, o: number) => u32le(b, o + 4) * 4294967296 + u32le(b, o);
export const i16le = (b: Uint8Array, o: number) => (u16le(b, o) << 16) >> 16;
export const i16be = (b: Uint8Array, o: number) => (u16be(b, o) << 16) >> 16;
/** 32-bit synchsafe integer (ID3v2): 4 bytes × 7 bits. */
export const synchsafe32 = (b: Uint8Array, o: number) =>
  ((b[o] & 0x7f) << 21) | ((b[o + 1] & 0x7f) << 14) | ((b[o + 2] & 0x7f) << 7) | (b[o + 3] & 0x7f);

/**
 * Reads bits MSB-first from an absolute bit position and records each read as a
 * Field. Use it for packed headers (MP3/ADTS/FLAC frame headers, AudioSpecificConfig).
 */
export class BitFields {
  readonly fields: Field[] = [];
  pos: number; // absolute bit position
  constructor(private readonly b: Uint8Array, byteOffset: number, bitOffset = 0) {
    this.pos = byteOffset * 8 + bitOffset;
  }
  /** Reads n bits (n <= 53, exact in a JS number) without recording a field. */
  peek(n: number): number {
    let v = 0;
    let p = this.pos;
    for (let i = 0; i < n; i++, p++) {
      const byte = this.b[p >> 3] ?? 0;
      v = v * 2 + ((byte >> (7 - (p & 7))) & 1);
    }
    return v;
  }
  skip(n: number): void {
    this.pos += n;
  }
  /** Reads n bits and advances, without recording a field. */
  bits(n: number): number {
    const v = this.peek(n);
    this.pos += n;
    return v;
  }
  /**
   * Reads n bits and records a field. `fmt` turns the raw number into the shown
   * value (default: decimal).
   */
  read(name: string, n: number, fmt?: (v: number) => string, note?: string): number {
    const bit = this.pos;
    const v = this.bits(n);
    this.fields.push({ name, value: fmt ? fmt(v) : String(v), raw: v, bit, bits: n, note });
    return v;
  }
  get byte(): number {
    return this.pos >> 3;
  }
}

/** Plain bit reader (no field recording) for hot paths such as FLAC subframe decoding. */
export class BitReader {
  pos: number;
  constructor(private readonly b: Uint8Array, byteOffset: number, public readonly endByte = b.length) {
    this.pos = byteOffset * 8;
  }
  bits(n: number): number {
    let v = 0;
    for (let i = 0; i < n; i++) {
      const p = this.pos++;
      v = v * 2 + ((this.b[p >> 3] >> (7 - (p & 7))) & 1);
    }
    return v;
  }
  signed(n: number): number {
    if (n === 0) return 0;
    const v = this.bits(n);
    return v >= 2 ** (n - 1) ? v - 2 ** n : v;
  }
  unary(): number {
    let n = 0;
    while (this.bits(1) === 0) {
      n++;
      if (this.pos >> 3 >= this.endByte) throw new Error('unary run past end');
    }
    return n;
  }
  align(): void {
    this.pos = (this.pos + 7) & ~7;
  }
  get byte(): number {
    return this.pos >> 3;
  }
}

/** Helper for byte-aligned fields. */
export function field(name: string, value: string | number, offset: number, size: number, note?: string, raw?: number | bigint): Field {
  return {
    name,
    value: typeof value === 'number' ? value.toLocaleString('en-US') : value,
    raw: raw ?? (typeof value === 'number' ? value : undefined),
    bit: offset * 8,
    bits: size * 8,
    note,
  };
}

export const hex = (v: number, width = 2) => '0x' + v.toString(16).toUpperCase().padStart(width, '0');
export const plural = (n: number, word: string) => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;
export const fmtHz = (hz: number) => (hz >= 1000 ? `${+(hz / 1000).toFixed(3)} kHz` : `${hz} Hz`);
export const fmtKbps = (bps: number) => `${Math.round(bps / 1000).toLocaleString('en-US')} kbps`;
export const fmtBytes = (n: number) =>
  n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(2)} MB`;
export const fmtSec = (s: number) => {
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r.toFixed(3).padStart(6, '0')}`;
};

/** Common names for channel counts. */
export function channelName(n: number): string {
  return ({ 1: 'Mono', 2: 'Stereo', 3: '3.0', 4: 'Quad', 5: '5.0', 6: '5.1', 7: '6.1', 8: '7.1' } as Record<number, string>)[n] ?? `${n} channels`;
}
